import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';

// Sportcomplex › Clubs: the clubs that say they are housed at the complex. Staff approve
// a request (the club's reports on the complex's places then come here), decline it, or
// end a link.

const ERRORS = {
  not_found: 'Deze club is niet (meer) aangesloten.',
  not_requested: 'Deze club is al aangesloten.',
  not_linked: 'Deze club is nog niet aangesloten.',
  forbidden: 'Dat mag je niet.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';
const fmtDate = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function ComplexClubsView() {
  const [clubs, setClubs] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api('/api/complexes/clubs').then(setClubs).catch(err => { setClubs([]); setError(message(err)); }); }, []);

  async function decide(club, decision) {
    const ask = {
      decline: `De aanvraag van ${club.club_name} afwijzen?`,
      unlink: `De aansluiting van ${club.club_name} beëindigen? Hun nieuwe meldingen op het complex komen dan niet meer hier binnen.`,
    }[decision];
    if (ask && !confirm(ask)) return;
    setBusy(club.club_id); setError(null);
    try { setClubs(await api(`/api/complexes/clubs/${club.club_id}/${decision}`, { method: 'POST' })); }
    catch (err) { setError(message(err)); }
    setBusy(null);
  }

  if (!clubs) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  const requests = clubs.filter(c => c.status === 'aangevraagd');
  const linked = clubs.filter(c => c.status === 'actief');
  return (
    <div>
      <div class="page-header" style="margin-bottom:16px">
        <h1 class="page-title">Clubs</h1>
      </div>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      {requests.length > 0 && (
        <Card title={`Aanvragen (${requests.length})`} accent>
          <div class="text-sm text-muted" style="margin-bottom:8px">Deze clubs melden dat ze op het complex gehuisvest zijn. Na je goedkeuring komen hun storingen aan velden, kleedkamers en andere plekken van het complex bij jou binnen.</div>
          {requests.map(c => (
            <div key={c.club_id} style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--color-border)">
              <span style="flex:1 1 200px;min-width:0"><strong>{c.club_name}</strong> <span class="text-sm text-muted">· aangevraagd {fmtDate(c.requested_at)}{c.requested_by_name ? ` door ${c.requested_by_name}` : ''}</span></span>
              <Button size="sm" disabled={busy === c.club_id} onClick={() => decide(c, 'approve')}>Goedkeuren</Button>
              <Button size="sm" variant="secondary" disabled={busy === c.club_id} onClick={() => decide(c, 'decline')}>Afwijzen</Button>
            </div>
          ))}
        </Card>
      )}
      <Card title={`Aangesloten clubs (${linked.length})`} style={requests.length ? 'margin-top:16px' : ''}>
        {linked.length === 0 && (
          <div class="text-sm text-muted">
            Nog geen clubs aangesloten. Een club met Vereniging4U sluit zich aan via Instellingen › Sportcomplex; je ziet de aanvraag dan hier.
          </div>
        )}
        {linked.map(c => (
          <div key={c.club_id} style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--color-border)">
            <span style="flex:1 1 200px;min-width:0">
              <strong>{c.club_name}</strong> <span class="text-sm text-muted">· aangesloten sinds {fmtDate(c.decided_at)}</span>
              <span class="text-sm text-muted" style="display:block">Bestuur: {c.board?.length ? c.board.join(', ') : 'nog niemand'}</span>
            </span>
            <Button size="sm" variant="secondary" disabled={busy === c.club_id} onClick={() => decide(c, 'unlink')}>Aansluiting beëindigen</Button>
          </div>
        ))}
      </Card>
      <GuestClubs />
    </div>
  );
}

// Clubs on the complex without Vereniging4U: their contact person gets the mededelingen
// by mail, and a reporter on the public form picks their club from this list
const GUEST_ERRORS = { name_required: 'Geef de club een naam.', invalid_email: 'Vul een geldig e-mailadres in.', too_many: 'Maximaal 100 gastclubs.' };
function GuestClubs() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null); // { id?, name, contact_name, contact_email }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('/api/complexes/guests').then(setList).catch(() => setList([])); }, []);
  async function run(fn) {
    setBusy(true); setError(null);
    try { setList(await fn()); setForm(null); } catch (err) { setError(GUEST_ERRORS[err.code] || message(err)); }
    setBusy(false);
  }
  const save = () => run(() => api(form.id ? `/api/complexes/guests/${form.id}` : '/api/complexes/guests', { method: form.id ? 'PUT' : 'POST', body: JSON.stringify(form) }));
  const remove = g => confirm(`${g.name} van de lijst halen?`) && run(() => api(`/api/complexes/guests/${g.id}`, { method: 'DELETE' }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  if (!list) return null;
  return (
    <Card title={`Gastclubs zonder Vereniging4U (${list.length})`} style="margin-top:16px">
      <div class="text-sm text-muted" style="margin-bottom:8px">Hun contactpersoon krijgt je mededelingen per mail, en wie via het meldformulier (QR) een storing meldt, kiest zijn club uit deze lijst.</div>
      {list.map(g => (
        <div key={g.id} style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--color-border)">
          <span style="flex:1 1 200px;min-width:0"><strong>{g.name}</strong><span class="text-sm text-muted" style="display:block">{[g.contact_name, g.contact_email].filter(Boolean).join(' · ') || 'geen contactpersoon'}</span></span>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setForm({ ...g, contact_name: g.contact_name || '', contact_email: g.contact_email || '' })}>Wijzigen</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => remove(g)}>Weghalen</Button>
        </div>
      ))}
      {form ? (
        <div style="display:flex;flex-direction:column;gap:8px;margin-top:10px;padding-top:10px;border-top:1px solid var(--color-border)">
          <input class="form-input" maxLength={100} value={form.name} onInput={e => set('name', e.target.value)} placeholder="Naam van de club" aria-label="Naam van de club" />
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <input class="form-input" style="flex:1 1 180px" maxLength={100} value={form.contact_name} onInput={e => set('contact_name', e.target.value)} placeholder="Contactpersoon" aria-label="Contactpersoon" />
            <input class="form-input" style="flex:1 1 200px" type="email" maxLength={200} value={form.contact_email} onInput={e => set('contact_email', e.target.value)} placeholder="E-mailadres" aria-label="E-mailadres" />
          </div>
          <div style="display:flex;gap:8px"><Button size="sm" disabled={busy || !form.name.trim()} onClick={save}>Opslaan</Button><Button size="sm" variant="secondary" onClick={() => { setForm(null); setError(null); }}>Annuleren</Button></div>
        </div>
      ) : (
        <div style="margin-top:10px"><Button size="sm" onClick={() => setForm({ name: '', contact_name: '', contact_email: '' })}>+ Gastclub</Button></div>
      )}
      {error && <div class="alert alert-error" style="margin-top:8px">{error}</div>}
    </Card>
  );
}
